import { CloseCircleOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { ColumnsType } from "antd/es/table";
import { t } from "i18next";
import DownloadProductionCodeRequestFileButton from "../../pages/ProductionCode/components/DownloadProductionCodesButton/DownloadProductionCodeRequestFileButton";
import { PaginationPayload } from "../../types";
import { FilterItem, FilterItemType } from "../filters/filters.types";
import { renderPlacenTag, renderStatusTag } from "./productionCode.functions";
import {
  ProductionCodeRequest,
  ProductionCodeRequestListPayload,
  ProductionCodeRequestStatus,
  ProdukcioniKodIzvod,
  ProdukcioniKodIzvodPayload,
} from "./productionCode.types";

export const PRODUCTION_CODE_REQUEST_INITIAL_FILTERS = {
  robnaMarkaId: null,
  datum: null,
  pib: null,
  brojZahteva: null,
};

export const PRODUCTION_CODE_FORM_INITIAL_VALUES = {
  oznakaKn: "",
  masinaId: null,
  objekatId: null,
  robnaMarkaId: null,
  ukljuceneMasine: true,
  duvanskiProizvodVrstaId: null,
  brojCigaretaUPojedinacnomPakovanju: null,
  prosecnaBrutoMasaJedinicnogPakovanja: null,
  trazenaKolicinaJedinstvenihOznakaPojedinacnogPakovanja: null,
};

export const PRODUKCIONI_KOD_IZVOD_INITIAL_FILTERS: ProdukcioniKodIzvodPayload =
  {
    datum: null,
    brojZahteva: null,
    pozivNaBroj: null,
    pageNumber: 1,
    pageSize: 10,
  };

export const PRODUKCIONI_KOD_IZVOD_INITIAL_PAGINATION: PaginationPayload = {
  pageNumber: 1,
  pageSize: 10,
};

export const getProductionCodeRequestTableColumns = ({
  hiddenColumns,
}: {
  hiddenColumns: (keyof ProductionCodeRequest)[];
}) => {
  const allColumns = [
    {
      title: t("TABLES.BROJ_ZAHTEVA"),
      dataIndex: "brojZahteva",
      key: "brojZahteva",
      width: "100px",
    },
    {
      title: t("PLACEHOLDERS.PIB"),
      width: "100px",
      key: "pib",
      dataIndex: "pib",
    },
    {
      title: t("TABLES.DRUSTVO_NAZIV"),
      dataIndex: "drustvo",
      key: "drustvo",
    },
    {
      title: t("TABLES.OBJEKAT"),
      dataIndex: "objekat",
      key: "objekat",
    },
    {
      title: t("TABLES.ROBNA_MARKA"),
      dataIndex: "robnaMarka",
      key: "robnaMarka",
    },
    {
      title: t("TABLES.TRAZENA_KOLICINA"),
      dataIndex: "trazenaKolicina",
      key: "trazenaKolicina",
      width: "100px",
    },
    {
      title: t("TABLES.DATUM_KREIRANJA_ZAHTEVA"),
      dataIndex: "datumKreiranjaZahteva",
      key: "datumKreiranjaZahteva",
    },
    {
      title: t("TABLES.STATUS"),
      key: "status",
      width: "120px",
      render: (record: ProductionCodeRequest) =>
        renderStatusTag(record?.statusId, record.status),
    },
    {
      title: t("TABLES.PLACEN"),
      key: "placen",
      width: "100px",
      render: (record: ProductionCodeRequest) => renderPlacenTag(record.placen),
    },
    {
      title: t("TOAST.PREUZIMANJE"),
      fixed: "right",
      align: "start",
      width: "210px",
      render: (record: ProductionCodeRequest) => {
        return record?.statusId === ProductionCodeRequestStatus.KREIRAN ? (
          <DownloadProductionCodeRequestFileButton
            id={record?.id}
            buttonProps={{ block: true }}
            type="proformaInvoice"
            docName={record?.brojZahteva}
            sifraRobneMarke={record?.sifraRobneMarke}
          />
        ) : record?.statusId === ProductionCodeRequestStatus.PRIHVACEN ? (
          <DownloadProductionCodeRequestFileButton
            id={record?.id}
            buttonProps={{ block: true }}
            docName={record?.brojZahteva}
            type="codes"
            sifraRobneMarke={record?.sifraRobneMarke}
          />
        ) : (
          <Flex align="center" justify="center" style={{ color: "lightgray" }}>
            <CloseCircleOutlined />
          </Flex>
        );
      },
    },
  ] as ColumnsType<ProductionCodeRequest>;

  return allColumns.filter(
    (column) =>
      !hiddenColumns?.includes(column.key as keyof ProductionCodeRequest)
  );
};

export const getProductionCodeRequestFilterItems = ({
  trademarks,
  hiddenFilters,
  isLoadingTrademarks,
}: {
  trademarks?: DefaultOptionType[];
  hiddenFilters?: (keyof ProductionCodeRequestListPayload)[];
  isLoadingTrademarks?: boolean;
}): Array<FilterItem<ProductionCodeRequestListPayload>> => {
  const allFilters = [
    {
      fieldName: "datum",
      type: FilterItemType.DATE_RANGE,
      props: {
        placeholder: [t("PLACEHOLDERS.DATUM_OD"), t("PLACEHOLDERS.DATUM_DO")],
        allowClear: true,
      },
    },
    {
      fieldName: "robnaMarkaId",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.IZABERI_BREND"),
        allowClear: true,
        showSearch: true,
        options: trademarks,
        loading: isLoadingTrademarks,
      },
    },
    {
      fieldName: "pib",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.PIB"),
        allowClear: true,
      },
    },
    {
      fieldName: "brojZahteva",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.BROJ_ZAHTEVA"),
        allowClear: true,
      },
    },
  ] as Array<FilterItem<ProductionCodeRequestListPayload>>;

  return allFilters.filter(
    (filter) => !hiddenFilters?.includes(filter.fieldName)
  );
};

export const getProdukcioniKodIzvodTableColumns = () => {
  return [
    {
      title: t("TABLES.BROJ_ZAHTEVA"),
      dataIndex: "brojZahteva",
      key: "brojZahteva",
      width: "150px",
    },
    {
      title: t("TABLES.DATUM_IZVODA"),
      dataIndex: "datumIzvoda",
      key: "datumIzvoda",
      width: "100px",
    },
    {
      title: t("TABLES.BROJ_IZVODA"),
      dataIndex: "brojIzvoda",
      key: "brojIzvoda",
      width: "120px",
    },
    {
      title: t("TABLES.UPLATILAC"),
      dataIndex: "uplatilac",
      key: "uplatilac",
      width: "120px",
    },
    {
      title: t("TABLES.POZIV_NA_BROJ"),
      dataIndex: "pozivNaBroj",
      key: "pozivNaBroj",
      width: "150px",
    },
    {
      title: t("TABLES.IZNOS"),
      dataIndex: "iznos",
      key: "iznos",
      width: "120px",
    },
  ] as ColumnsType<ProdukcioniKodIzvod>;
};

export const getProdukcioniKodIzvodFilterItems = (): Array<
  FilterItem<ProdukcioniKodIzvodPayload>
> => {
  return [
    {
      fieldName: "datum",
      type: FilterItemType.DATE_RANGE,
      props: {
        placeholder: [t("PLACEHOLDERS.DATUM_OD"), t("PLACEHOLDERS.DATUM_DO")],
        allowClear: true,
      },
    },
    {
      fieldName: "brojZahteva",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.BROJ_ZAHTEVA"),
        allowClear: true,
      },
    },
    {
      fieldName: "pozivNaBroj",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TABLES.POZIV_NA_BROJ"),
        allowClear: true,
      },
    },
  ] as Array<FilterItem<ProdukcioniKodIzvodPayload>>;
};
